const EBML_HEADER = [0x1a, 0x45, 0xdf, 0xa3]

let activeMediaRecorder = null

export function setActiveMediaRecorder(recorder) {
  activeMediaRecorder = recorder
}

export function getActiveMediaRecorder() {
  return activeMediaRecorder
}

export function clearActiveMediaRecorder() {
  activeMediaRecorder = null
}

export function getSupportedVideoMimeType() {
  if (typeof MediaRecorder === 'undefined') {
    return 'video/webm'
  }

  const types = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ]

  return types.find(type => MediaRecorder.isTypeSupported(type)) || 'video/webm'
}

export function getVideoFileExtension(mimeType) {
  if (mimeType && mimeType.includes('mp4')) {
    return 'mp4'
  }

  return 'webm'
}

export function getTotalChunkSize(chunks) {
  return chunks.reduce((sum, chunk) => sum + (chunk.size || 0), 0)
}

export function isValidRecording(chunks, minBytes = 1024) {
  return Array.isArray(chunks) && chunks.length > 0 && getTotalChunkSize(chunks) >= minBytes
}

export function createVideoBlob(chunks, mimeType) {
  return new Blob(chunks, { type: mimeType || 'video/webm' })
}

function bytesStartWithEbml(bytes) {
  return bytes.length >= 4
    && bytes[0] === EBML_HEADER[0]
    && bytes[1] === EBML_HEADER[1]
    && bytes[2] === EBML_HEADER[2]
    && bytes[3] === EBML_HEADER[3]
}

function findEbmlOffset(bytes) {
  for (let i = 0; i <= bytes.length - 4; i++) {
    if (bytes[i] === EBML_HEADER[0]
      && bytes[i + 1] === EBML_HEADER[1]
      && bytes[i + 2] === EBML_HEADER[2]
      && bytes[i + 3] === EBML_HEADER[3]) {
      return i
    }
  }
  return -1
}

export async function blobStartsWithEbml(blob) {
  const header = await blob.slice(0, 4).arrayBuffer()
  return bytesStartWithEbml(new Uint8Array(header))
}

async function findEbmlChunkIndex(chunks) {
  for (let i = 0; i < chunks.length; i++) {
    const header = await chunks[i].slice(0, 4).arrayBuffer()
    if (bytesStartWithEbml(new Uint8Array(header))) {
      return i
    }
  }
  return -1
}

export async function buildPlayableVideoBlob(chunks, mimeType) {
  if (!isValidRecording(chunks)) {
    return null
  }

  const type = mimeType || 'video/webm'
  const ebmlChunkIndex = await findEbmlChunkIndex(chunks)
  const orderedChunks = ebmlChunkIndex > 0
    ? chunks.slice(ebmlChunkIndex)
    : chunks

  let blob = createVideoBlob(orderedChunks, type)

  if (await blobStartsWithEbml(blob)) {
    return blob
  }

  const bytes = new Uint8Array(await blob.arrayBuffer())
  const offset = findEbmlOffset(bytes)

  if (offset > 0) {
    return new Blob([bytes.slice(offset)], { type })
  }

  return null
}

export function stopExistingRecorder() {
  const recorder = activeMediaRecorder

  if (!recorder || recorder.state === 'inactive') {
    clearActiveMediaRecorder()
    return
  }

  recorder.ondataavailable = null
  recorder.onstop = null

  try {
    recorder.stop()
  } catch (e) {
    // Ignore stale recorder stop errors.
  }

  clearActiveMediaRecorder()
}

export function createMediaRecorder(stream, isLongVideo) {
  stopExistingRecorder()

  const mimeType = getSupportedVideoMimeType()
  let options = { mimeType, videoBitsPerSecond: 1000000 }

  if (typeof MediaRecorder !== 'undefined' && !MediaRecorder.isTypeSupported(mimeType)) {
    options = { videoBitsPerSecond: 1000000 }
  }

  const recorder = new MediaRecorder(stream, options)

  return {
    recorder,
    mimeType: recorder.mimeType || mimeType,
    useTimeslice: isLongVideo,
  }
}
